import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BookService } from './services/bookService';
import { BookContentDetail } from './content-book/BookContentDetail';
import {
  EditBookDialog,
  DuplicateBookDialog,
  CoverImageEditDialog,
  DescriptionEditDialog,
  BookOverviewTab
} from './components';

const getBookPrice = (book: any): number => {
  const value = book?.price;
  const rawPrice =
    value && typeof value === 'object' ? value.amount : value;
  const price = Number(rawPrice);

  return Number.isFinite(price) && price >= 0 ? price : 0;
};

const BookDetailPage: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // No longer need tab state since we're merging tabs
  
  // Get dialog states from URL params
  const editDialogOpen = searchParams.get('editBook') === 'true';
  const duplicateDialogOpen = searchParams.get('duplicate') === 'true';
  
  const [book, setBook] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add state for edit form
  const [editFormData, setEditFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    name: '',
    authors: [] as string[],
    publisher: '',
    price: 0,
    language: '',
    categories: [] as string[],
    tags: [] as string[]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);


  // Add state for cover image edit dialog
  const [coverEditDialogOpen, setCoverEditDialogOpen] = useState(false);
  const [coverEditFormData, setCoverEditFormData] = useState({
    cover_image_url: "",
    coverImageFile: null as File | null,
  });
  const [coverEditPreview, setCoverEditPreview] = useState<string | null>(null);
  const [isSavingCover, setIsSavingCover] = useState(false);

  // Add state for description edit dialog
  const [descriptionEditDialogOpen, setDescriptionEditDialogOpen] = useState(false);
  const [descriptionEditFormData, setDescriptionEditFormData] = useState({
    description: ''
  });
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Add state for export loading
  const [isExporting, setIsExporting] = useState(false);

  // Add state for approval actions
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Add state for duplicate (clone) action
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Add state for published book data
  const [publishedBook, setPublishedBook] = useState<any>(null);
  const [isLoadingPublished, setIsLoadingPublished] = useState(false);

  // Function to update URL params without full navigation
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };

  // No longer need tab change handler since we're merging tabs

  // Function to open edit dialog
  const openEditDialog = () => {
    // Clear any previous errors and success messages
    setError(null);
    setSuccessMessage(null);
    
    // Initialize form data with current book data
    setEditFormData({
      title: book.title || '',
      subtitle: book.subtitle || '',
      description: book.description || '',
      name: book.title || book.name || '',
      authors: book.authors || [],
      publisher: book.publisher || '',
      price: getBookPrice(book),
      language: book.language || '',
      categories: book.categories || [],
      tags: book.tags || []
    });
    
    updateSearchParams({ editBook: 'true' });
  };

  // Function to close edit dialog
  const closeEditDialog = () => {
    updateSearchParams({ editBook: null });
  };

  // Function to open duplicate dialog
  const openDuplicateDialog = () => {
    updateSearchParams({ duplicate: 'true' });
  };

  // Function to close duplicate dialog
  const closeDuplicateDialog = () => {
    updateSearchParams({ duplicate: null });
  };

  // Function to handle duplicating (cloning) the book via API
  const handleDuplicateBook = async (newData: any) => {
    if (!bookId) return;

    if (!newData?.title?.trim()) {
      toast.error('Tên sách mới không được để trống');
      return;
    }

    try {
      setIsDuplicating(true);
      const response = await BookService.cloneBook(bookId, newData);

      // Backend may return the new book directly or wrapped in a `data` field
      const newBookId =
        response?.data?.id || response?.data?._id || response?.id || response?._id;

      toast.success('Sao chép sách thành công!');
      closeDuplicateDialog();

      if (newBookId) {
        navigate(`/books/${newBookId}`);
      } else {
        // Fallback: no id returned, go back to the book list
        navigate('/books/list');
      }
    } catch (err: any) {
      console.error('Clone book failed:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Sao chép sách thất bại';
      toast.error(`Lỗi sao chép: ${msg}`);
    } finally {
      setIsDuplicating(false);
    }
  };

  // Function to handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If updating title, also update name for API compatibility
    if (field === 'title') {
      setEditFormData(prev => ({
        ...prev,
        name: value
      }));
    }
    

    
    // Clear error message when user starts editing
    if (error) {
      setError(null);
    }
  };

  // Function to handle array field changes (authors, categories, tags)
  const handleArrayFieldChange = (field: string, value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setEditFormData(prev => ({
      ...prev,
      [field]: items
    }));
    
    // Clear error message when user starts editing
    if (error) {
      setError(null);
    }
  };



  // Function to remove item from array field
  const handleRemoveArrayItem = (field: string, index: number) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: (prev[field as keyof typeof prev] as string[]).filter((_, i) => i !== index)
    }));
    
    // Clear error message when user starts editing
    if (error) {
      setError(null);
    }
  };

  



  // Function to open cover edit dialog
  const openCoverEditDialog = () => {
    setError(null);
    setSuccessMessage(null);
    
    setCoverEditFormData({
      cover_image_url: book.cover_image_url || "",
      coverImageFile: null,
    });
    setCoverEditPreview(book.cover_image_url || null);
    setCoverEditDialogOpen(true);
  };

  // Function to close cover edit dialog
  const closeCoverEditDialog = () => {
    setCoverEditDialogOpen(false);
    setCoverEditFormData({
      cover_image_url: "",
      coverImageFile: null,
    });
    setCoverEditPreview(null);
  };

  // Function to handle cover edit form input changes
  const handleCoverEditInputChange = (field: string, value: string) => {
    setCoverEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (field === "cover_image_url") {
      setCoverEditPreview(value);
    }
    
    if (error) {
      setError(null);
    }
  };

  // Function to handle cover image file upload in edit dialog
  const handleCoverEditImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Vui lòng chọn file hình ảnh hợp lệ');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Kích thước file không được vượt quá 5MB');
        return;
      }
      
      setCoverEditFormData((prev) => ({
        ...prev,
        coverImageFile: file,
        cover_image_url: "", // Clear URL when file is selected
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverEditPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear error message
      if (error) {
        setError(null);
      }
    }
  };

  // Function to clear cover image in edit dialog
  const clearCoverEditImage = () => {
    setCoverEditFormData({
      cover_image_url: "",
      coverImageFile: null,
    });
    setCoverEditPreview(null);
  };

  // Function to save only cover image changes
  const handleSaveCoverImage = async () => {
    if (!bookId) return;
    
    try {
      setIsSavingCover(true);
      setError(null);
      setSuccessMessage(null);
      
      let coverImageUrl = '';
      
      // Handle cover image update
      if (coverEditFormData.coverImageFile) {
        
        try {
          // Upload cover image
          const uploadResponse = await BookService.uploadFile(coverEditFormData.coverImageFile);
          if (uploadResponse?.result?.[0]) {
            coverImageUrl = uploadResponse.result[0].url;
          }
        } catch (uploadError) {
          console.error('Failed to upload cover image:', uploadError);
          setError('Không thể tải lên ảnh bìa. Vui lòng thử lại.');
          return;
        }
      } else if (coverEditFormData.cover_image_url) {
        // If there's a URL, use it directly
        coverImageUrl = coverEditFormData.cover_image_url;
      }
      
      // Update book with new cover image
      const updateData = { cover_image_url: coverImageUrl };
      
      try {
        await BookService.updateBook(bookId, updateData);
      } catch (error) {
        console.error('Failed to update book cover:', error);
        throw error;
      }
      
      // Update local state with new cover image
      setBook((prev: any) => ({
        ...prev,
        cover_image_url: coverImageUrl,
      }));
      
      // Show success message
      setSuccessMessage('Cập nhật ảnh bìa sách thành công!');
      
      // Close dialog after a short delay to show success message
      setTimeout(() => {
        closeCoverEditDialog();
        setSuccessMessage(null);
      }, 1500);
      
    } catch (err: any) {
      console.error('Failed to save cover image:', err);
      
      // Handle API errors
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(`Lỗi API: ${errorMessages.join(', ')}`);
      } else if (err.response?.data?.message) {
        setError(`Lỗi API: ${err.response.data.message}`);
      } else if (err.message) {
        setError(`Lỗi: ${err.message}`);
      } else {
        setError('Không thể cập nhật ảnh bìa sách. Vui lòng thử lại.');
      }
    } finally {
      setIsSavingCover(false);
    }
  };

  // Function to open description edit dialog
  const openDescriptionEditDialog = () => {
    setError(null);
    setSuccessMessage(null);
    
    setDescriptionEditFormData({
      description: book.description || ''
    });
    setDescriptionEditDialogOpen(true);
  };

  // Function to close description edit dialog
  const closeDescriptionEditDialog = () => {
    setDescriptionEditDialogOpen(false);
    setDescriptionEditFormData({
      description: ''
    });
  };

  // Function to save only description changes
  const handleSaveDescription = async () => {
    if (!bookId) return;
    
    try {
      setIsSavingDescription(true);
      setError(null);
      setSuccessMessage(null);
      
      // Update book with new description
      const updateData = { description: descriptionEditFormData.description };
      
      try {
        await BookService.patchBook(bookId, updateData);
      } catch (error) {
        console.error('Failed to update book description:', error);
        throw error;
      }
      
      // Update local state with new description
      setBook((prev: any) => ({
        ...prev,
        description: descriptionEditFormData.description
      }));
      
      // Show success message
      setSuccessMessage('Cập nhật mô tả sách thành công!');
      
      // Close dialog after a short delay to show success message
      setTimeout(() => {
        closeDescriptionEditDialog();
        setSuccessMessage(null);
      }, 1500);
      
    } catch (err: any) {
      console.error('Failed to save description:', err);
      
      // Handle API errors
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(`Lỗi API: ${errorMessages.join(', ')}`);
      } else if (err.response?.data?.message) {
        setError(`Lỗi API: ${err.response.data.message}`);
      } else if (err.message) {
        setError(`Lỗi: ${err.message}`);
      } else {
        setError('Không thể cập nhật mô tả sách. Vui lòng thử lại.');
      }
    } finally {
      setIsSavingDescription(false);
    }
  };

  // Function to export book to JSON
  const handleExportBook = async () => {
    if (!book) return;
    
    try {
      setIsExporting(true);
      setError(null);
      
      // Prepare book data for export
      const exportData = {
        ...book,
        exportedAt: new Date().toISOString(),
        exportedFrom: 'iBook Admin System'
      };
      
      // Convert to JSON string with proper formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob with JSON data
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename with book title and current date
      const fileName = `${book.title || 'book'}_${new Date().toISOString().split('T')[0]}.json`
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      setSuccessMessage('Xuất sách thành công!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Failed to export book:', error);
      setError('Không thể xuất sách. Vui lòng thử lại.');
    } finally {
      setIsExporting(false);
    }
  };

  // Function to submit for approval
  const handleSubmitApproval = async () => {
    if (!book) return;
    
    try {
      setIsSubmittingApproval(true);
      setError(null);
      
      // Update book status to pending_approval
      const updateData = { status: 'pending_approval' };
      
      try {
        await BookService.patchBook(book.id, updateData);
      } catch (error) {
        console.error('Failed to submit for approval:', error);
        throw error;
      }
      
      // Update local state
      setBook((prev: any) => ({
        ...prev,
        status: 'pending_approval'
      }));
      
      // Show success message
      setSuccessMessage('Gửi phê duyệt thành công!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      setError('Không thể gửi phê duyệt. Vui lòng thử lại.');
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // Function to approve book
  const handleApprove = async () => {
    if (!book) return;
    
    try {
      setIsApproving(true);
      setError(null);
      
      // Update book status to approved
      const updateData = { status: 'approved' };
      
      try {
        await BookService.patchBook(book.id, updateData);
      } catch (error) {
        console.error('Failed to approve book:', error);
        throw error;
      }
      
      // Update local state
      setBook((prev: any) => ({
        ...prev,
        status: 'approved'
      }));
      
      // Show success message
      setSuccessMessage('Phê duyệt sách thành công!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Failed to approve book:', error);
      setError('Không thể phê duyệt sách. Vui lòng thử lại.');
    } finally {
      setIsApproving(false);
    }
  };

  // Function to publish book
  const handlePublish = async () => {
    if (!book) return;
    
    try {
      setIsPublishing(true);
      setError(null);
      
      // Use the new publish API that saves to book_publishes collection
      const response = await BookService.publishBook(book.id);
      
      // Update local state with both original book and published book data
      setBook((prev: any) => ({
        ...prev,
        status: 'Đã xuất bản'
      }));
      
      // Store published book data for reference
      if (response.published_book) {
        setPublishedBook(response.published_book);
      } else {
        // If no published book data in response, refresh it
        await loadPublishedBookData();
      }
      
      // Show success message with more details
      const publishMessage = response.published_book 
        ? `Xuất bản sách thành công! Phiên bản ${response.published_book.version} đã được lưu.`
        : 'Xuất bản sách thành công!';
      setSuccessMessage(publishMessage);
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error: any) {
      console.error('Failed to publish book:', error);
      
      // Handle specific API errors
      if (error.response?.data?.message) {
        setError(`Lỗi xuất bản: ${error.response.data.message}`);
      } else if (error.message) {
        setError(`Lỗi: ${error.message}`);
      } else {
        setError('Không thể xuất bản sách. Vui lòng thử lại.');
      }
    } finally {
      setIsPublishing(false);
    }
  };

  // Function to save book changes
  const handleSaveBook = async () => {
    if (!bookId) return;
    
    // Basic validation
    if (!editFormData.title.trim()) {
      setError('Tên sách không được để trống');
      return;
    }

    if (!Number.isFinite(Number(editFormData.price)) || Number(editFormData.price) < 0) {
      setError('Giá phải là một số lớn hơn hoặc bằng 0');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      // Prepare update data: only send the fields that are actually being updated
      const updateData: any = {
        title: editFormData.title,
        subtitle: editFormData.subtitle,
        description: editFormData.description,
        // Map title to name if the API expects 'name' field
        name: editFormData.name,
        authors: editFormData.authors,
        publisher: editFormData.publisher,
        price: Number(editFormData.price),
        language: editFormData.language,
        categories: editFormData.categories,
        tags: editFormData.tags
      };
      
      let response;
      
      // Use PATCH method for partial update (no validation on backend)
      try {
        console.log('Attempting PATCH update with data:', updateData);
        response = await BookService.updateBook(bookId, updateData);
        console.log('PATCH update successful:', response);
      } catch (error) {
        console.error('PATCH update failed:', error);
        throw error; // Re-throw to be handled by outer catch block
      }
      
      // Update local state with new data
      setBook((prev: any) => ({
        ...prev,
        title: editFormData.title,
        subtitle: editFormData.subtitle,
        description: editFormData.description,
        name: editFormData.name,
        authors: editFormData.authors,
        publisher: editFormData.publisher,
        price: Number(editFormData.price),
        language: editFormData.language,
        categories: editFormData.categories,
        tags: editFormData.tags
      }));
      
      // Show success message
      setSuccessMessage('Cập nhật thông tin sách thành công!');
      
      // Close dialog after a short delay to show success message
      setTimeout(() => {
        closeEditDialog();
        setSuccessMessage(null);
      }, 1500);
      
    } catch (err: any) {
      console.error('All update methods failed:', err);
      
      // Handle API errors
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(`Lỗi API: ${errorMessages.join(', ')}`);
      } else if (err.response?.data?.message) {
        setError(`Lỗi API: ${err.response.data.message}`);
      } else if (err.message) {
        setError(`Lỗi: ${err.message}`);
      } else {
        setError('Không thể cập nhật thông tin sách. Vui lòng thử lại.');
      }
      
      // Keep dialog open when there are errors - don't close it
      // User can fix the errors and try again
    } finally {
      setIsSaving(false);
    }
  };

  // Function to check if form has changes
  const hasChanges = () => {
    return editFormData.title !== (book?.title || '') || 
           editFormData.subtitle !== (book?.subtitle || '') ||
           editFormData.name !== (book?.name || book?.title || '') ||
           JSON.stringify(editFormData.authors) !== JSON.stringify(book?.authors || []) ||
           editFormData.publisher !== (book?.publisher || '') ||
           Number(editFormData.price) !== getBookPrice(book) ||
           editFormData.language !== (book?.language || '') ||
           JSON.stringify(editFormData.categories) !== JSON.stringify(book?.categories || []) ||
           JSON.stringify(editFormData.tags) !== JSON.stringify(book?.tags || []);
  };

  // Function to check if cover edit form has changes
  const hasCoverChanges = () => {
    return (
      coverEditFormData.cover_image_url !== (book?.cover_image_url || "") ||
      coverEditFormData.coverImageFile !== null
    );
  };

  // Function to check if description edit form has changes
  const hasDescriptionChanges = () => {
    return descriptionEditFormData.description !== (book?.description || '');
  };

  // Function to load published book data
  const loadPublishedBookData = async () => {
    if (!bookId) return;
    
    try {
      setIsLoadingPublished(true);
      const response = await BookService.getPublishedBook(bookId);
      if (response.success && response.data) {
        setPublishedBook(response.data);
      }
    } catch (error) {
      // This is not an error - just means no published version exists yet
    } finally {
      setIsLoadingPublished(false);
    }
  };

  // No longer need to set default tab since we're merging tabs

  // Fetch book details from API
  useEffect(() => {
    const fetchBookDetails = async () => {
      if (!bookId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await BookService.getBookDetails(bookId);
        const bookData = response.data || response;
        
        setBook(bookData);
        
        // Also load published book data if available
        await loadPublishedBookData();
      } catch (err: any) {
        console.error('Failed to fetch book details:', err);
        setError('Không thể tải thông tin sách. Vui lòng thử lại.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookDetails();
  }, [bookId]);

  if (!bookId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-600 mb-4">Không tìm thấy ID sách</div>
          <Link to="/books/list" className="text-indigo-600 hover:text-indigo-900">
            Quay lại danh sách sách
          </Link>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Đang tải thông tin sách...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-red-600 mb-4">{error}</div>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Thử lại
            </button>
            <Link 
              to="/books/list" 
              className="text-indigo-600 hover:text-indigo-900 px-4 py-2"
            >
              Quay lại danh sách sách
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show not found if no book data
  if (!book) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-600 mb-4">Không tìm thấy sách</div>
          <Link to="/books/list" className="text-indigo-600 hover:text-indigo-900">
            Quay lại danh sách sách
          </Link>
        </div>
      </div>
    );
  }



  // No longer need tabs array since we're merging tabs

  return (
    <div className="w-full">
      {/* Breadcrumb */}
      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/books/list" className="text-gray-700 hover:text-gray-900">
              Sách
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-1 text-gray-500 md:ml-2" aria-current="page">
                {book.title}
              </span>
            </div>
          </li>
        </ol>
      </nav>



      {/* No longer need tabs navigation since we're merging content */}

      {/* Merged Content - Both Overview and Content */}
      <div className="space-y-8">
        {/* Book Overview Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Thông tin sách</h2>
          <BookOverviewTab
            book={book}
            onEditBook={openEditDialog}
            onEditCover={openCoverEditDialog}
            onEditDescription={openDescriptionEditDialog}
            onDuplicateBook={openDuplicateDialog}
            onExportBook={handleExportBook}
            isExporting={isExporting}
            onSubmitApproval={handleSubmitApproval}
            onApprove={handleApprove}
            onPublish={handlePublish}
            isPublishing={isPublishing}
            publishedBook={publishedBook}
            isLoadingPublished={isLoadingPublished}
          />
        </div>


        {/* Book Content Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Nội dung sách</h2>
          <BookContentDetail bookId={book.id} />
        </div>
      </div>

      {/* Edit Dialog */}
      <EditBookDialog
        isOpen={editDialogOpen}
        onClose={closeEditDialog}
        onSave={handleSaveBook}
        book={book}
        editFormData={editFormData}
        onInputChange={handleInputChange}
        onArrayFieldChange={handleArrayFieldChange}
        onRemoveArrayItem={handleRemoveArrayItem}
        isSaving={isSaving}
        hasChanges={hasChanges}
        error={error}
        successMessage={successMessage}
      />


      {/* Duplicate Dialog */}
      <DuplicateBookDialog
        isOpen={duplicateDialogOpen}
        onClose={closeDuplicateDialog}
        onDuplicate={handleDuplicateBook}
        isDuplicating={isDuplicating}
        book={book}
      />


      {/* Cover Image Edit Dialog */}
      <CoverImageEditDialog
        isOpen={coverEditDialogOpen}
        onClose={closeCoverEditDialog}
        onSave={handleSaveCoverImage}
        coverEditFormData={coverEditFormData}
        onInputChange={handleCoverEditInputChange}
        onImageUpload={handleCoverEditImageUpload}
        onClearImage={clearCoverEditImage}
        coverEditPreview={coverEditPreview}
        isSaving={isSavingCover}
        hasChanges={hasCoverChanges}
        error={error}
        successMessage={successMessage}
      />


      {/* Description Edit Dialog */}
      <DescriptionEditDialog
        isOpen={descriptionEditDialogOpen}
        onClose={closeDescriptionEditDialog}
        onSave={handleSaveDescription}
        description={descriptionEditFormData.description}
        onDescriptionChange={(value) => setDescriptionEditFormData(prev => ({ ...prev, description: value }))}
        isSaving={isSavingDescription}
        hasChanges={hasDescriptionChanges}
        error={error}
        successMessage={successMessage}
      />

    </div>
  );
};

export default BookDetailPage;
